import * as Sentry from '@sentry/react';
import { hotjar } from 'react-hotjar';
import { matchPath, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-tooltip/dist/react-tooltip.css';

import routesConfig from '@/config/routes.config';
import AppRoutes from '@/routes';
import FallbackComponent from './components/placeholders/fallback-component';
import ErrorBoundary from './error-boundary';
import FilterProvider from './features/filter/filter-provider';
import useUserLocation from './hooks/use-user-location.hook';
import DeprecatedPage from './pages/deprecated.page';
import UnderMaintenance from './pages/maintenance.page';

const USER_ID = import.meta.env.VITE_HOTJAR_SITE_ID;
const VERSION = import.meta.env.VITE_HOTJAR_SITE_VERSION;
const UNDER_MAINTENANCE = import.meta.env.VITE_UNDER_MAINTENANCE || false;

const LegacyApp = () => {
  useUserLocation();
  hotjar.initialize(USER_ID, VERSION);

  return (
    <Sentry.ErrorBoundary fallback={<FallbackComponent />} showDialog>
      <FilterProvider>
        <ErrorBoundary>
          {UNDER_MAINTENANCE === 'true' ? <UnderMaintenance /> : <AppRoutes />}
          <ToastContainer />
        </ErrorBoundary>
      </FilterProvider>
    </Sentry.ErrorBoundary>
  );
};

const App: React.FC = () => {
  const { pathname } = useLocation();

  // This dashboard is deprecated: every route sends users to the new one.
  // The one exception is the extension login handoff — the new dashboard
  // still opens app.nexlev.io/#/extension-login?accessToken=…&refreshToken=…
  // for the Chrome extension, so that route must keep working here.
  if (!matchPath(routesConfig.EXTENSION_LOGIN, pathname)) {
    return <DeprecatedPage />;
  }

  return <LegacyApp />;
};

export default Sentry.withProfiler(App);
